import React, { forwardRef, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';

import ConfirmDialog from '../../dialogs/ConfirmDialog';

/**
 * Delete confirmation dialog for infrastructure items
 * Used by both List and Detail pages
 *
 * Uses React.forwardRef pattern to expose imperative handle methods.
 * Parent components can call ref.current.open(idCount) to show the dialog.
 *
 * @param {Object} props
 * @param {Function} props.handleDeleteDataTableAction - Callback when delete is confirmed
 * @param {React.Ref} ref - Forwarded ref with imperative handle methods: open(idCount), close()
 *
 * @example
 * const dialogRef = useRef();
 * <DeleteConfirmDialog ref={dialogRef} handleDeleteDataTableAction={handleDelete} />
 * // Later: dialogRef.current.open(5); // Show dialog for 5 items
 */
const DeleteConfirmDialog = forwardRef(({ handleDeleteDataTableAction }, ref) => {
    const [showDialog, setShowDialog] = useState(false);
    const [idCount, setIdCount] = useState(0);

    useImperativeHandle(ref, () => ({
        open: (count) => {
            setIdCount(count);
            setShowDialog(true);
        },
        close: () => setShowDialog(false),
    }));

    return (
        <ConfirmDialog
            idCount={idCount}
            open={showDialog}
            setOpen={setShowDialog}
            onConfirm={handleDeleteDataTableAction}
            label="DELETE"
        />
    );
});

DeleteConfirmDialog.displayName = 'DeleteConfirmDialog';

DeleteConfirmDialog.propTypes = {
    handleDeleteDataTableAction: PropTypes.func.isRequired,
};

export default DeleteConfirmDialog;
